// src/pages/admin/workspace/useWorkspaceData.js
//
// The single source of authorized items for the redesigned workspace.
//
// Every access decision below is the ORIGINAL DashboardSelector's, copied
// verbatim (same functions, same arguments, same conditions):
//   - resolveDashboardAccess() + settings.dashboardRegistry -> internal dashboards
//   - shouldAlwaysShowDashboardSelector() + skipSelectorWhenSingle -> redirect
//   - getWorkspaceItems(role, email) -> portals / admin tools / other tools
//   - readQuickAccessIds() / writeQuickAccessIds() -> favourites
// Nothing here widens access. Categories are labels added afterwards.
import { useCallback, useMemo, useState } from "react";
import {
  Building2, Calendar, LayoutDashboard,
} from "lucide-react";
import {
  resolveDashboardAccess,
  shouldAlwaysShowDashboardSelector,
} from "../../../utils/dashboardAccess";
import {
  MAX_QUICK_ACCESS,
  PUBLIC_FORMS_ITEM,
  getWorkspaceItems,
  readQuickAccessIds,
  writeQuickAccessIds,
} from "../../../config/campusPortals";
import { categoryForSource } from "./workspaceCategories";

// Colour identity per dashboardRegistry key (same mapping as the original).
const DASHBOARD_UI = {
  guestRoom: {
    title: "Guest Room Dashboard",
    icon: Building2,
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
  },
  venue: {
    title: "Venue Booking Dashboard",
    icon: Calendar,
    iconBg: "bg-purple-100",
    iconColor: "text-purple-600",
  },
  night: {
    title: "Night Pass Dashboard",
    icon: LayoutDashboard,
    iconBg: "bg-slate-100",
    iconColor: "text-slate-600",
  },
};

const tag = (source, items) =>
  items.map((item) => ({ ...item, category: categoryForSource(source, item) }));

export default function useWorkspaceData(currentUser, settings) {
  const role = (currentUser?.role || currentUser?.user?.role || "").toLowerCase();
  const email = currentUser?.email || currentUser?.user?.email || "";
  const [favoritesVersion, setFavoritesVersion] = useState(0);

  const dashboardAccess = useMemo(
    () => resolveDashboardAccess(currentUser || {}, settings),
    [currentUser, settings]
  );

  // Original single-dashboard redirect, unchanged.
  let redirectTo = null;
  if (
    dashboardAccess.dashboards.length === 1 &&
    dashboardAccess.skipSelectorWhenSingle &&
    !shouldAlwaysShowDashboardSelector(role)
  ) {
    const onlyDashboard = (settings?.dashboardRegistry || []).find(
      (dashboard) => dashboard.key === dashboardAccess.dashboards[0]
    );
    if (onlyDashboard?.path) redirectTo = onlyDashboard.path;
  }

  const dashboards = useMemo(
    () =>
      (settings?.dashboardRegistry || [])
        .filter((dashboard) => dashboard.active && dashboardAccess.dashboards.includes(dashboard.key))
        .map((dashboard) => {
          const ui = DASHBOARD_UI[dashboard.key] || DASHBOARD_UI.guestRoom;
          return {
            id: dashboard.key,
            title: dashboard.label || ui.title,
            description: dashboard.description || ui.title,
            icon: ui.icon,
            iconBg: ui.iconBg,
            iconColor: ui.iconColor,
            target: { type: "route", path: dashboard.path },
          };
        }),
    [settings, dashboardAccess]
  );

  const { campusPortals, adminTools, otherTools } = useMemo(
    () => getWorkspaceItems(role, email),
    [role, email]
  );

  const favoritableItems = useMemo(
    () => [...campusPortals, ...adminTools, ...otherTools].filter((item) => item.favoritable),
    [campusPortals, adminTools, otherTools]
  );
  const quickAccessItems = useMemo(() => {
    const byId = new Map(favoritableItems.map((item) => [item.id, item]));
    const ids = readQuickAccessIds(role, favoritableItems.map((item) => item.id), email);
    return ids.map((id) => byId.get(id));
    // favoritesVersion re-reads localStorage after a toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, email, favoritableItems, favoritesVersion]);
  const favoriteIds = useMemo(
    () => new Set(quickAccessItems.map((item) => item.id)),
    [quickAccessItems]
  );

  const canFavorite = quickAccessItems.length < MAX_QUICK_ACCESS;
  const toggleFavorite = useCallback(
    (item) => {
      const currentIds = quickAccessItems.map((entry) => entry.id);
      const next = currentIds.includes(item.id)
        ? currentIds.filter((id) => id !== item.id)
        : currentIds.length < MAX_QUICK_ACCESS
        ? [...currentIds, item.id]
        : currentIds;
      writeQuickAccessIds(role, next, email);
      setFavoritesVersion((version) => version + 1);
    },
    [quickAccessItems, role, email]
  );

  // Every authorized item exactly once, labelled with a presentation category.
  const items = useMemo(
    () => [
      ...tag("dashboards", dashboards),
      ...tag("campusPortals", campusPortals),
      ...tag("adminTools", adminTools),
      ...tag("otherTools", otherTools),
      ...tag("footer", [PUBLIC_FORMS_ITEM]),
    ],
    [dashboards, campusPortals, adminTools, otherTools]
  );

  return {
    role,
    email,
    redirectTo,
    items,
    dashboards,
    favoritableItems,
    quickAccessItems,
    favoriteIds,
    canFavorite,
    toggleFavorite,
  };
}
